import { Box, Text } from '@factorialco/deprecated-design-system'
import {
  PerformanceProcessTemplateEditSettingsPageDocument,
  PerformanceReviewProcessReviewSettingsPageDocument,
  PerformanceScoreModeManagerDeleteDocument,
  PerformanceScoreModeManagerSetDocument,
  PerformanceValueObjectsQuestionnaireableEnum,
} from 'generated/resources/graphql'

import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { ScoreModeSelector, type PublicProps as SelectorProps } from './ScoreModeSelector'

type Questionnaireable = {
  questionnaireableId: number
  questionnaireableType: PerformanceValueObjectsQuestionnaireableEnum
}
type Props = Questionnaireable & SelectorProps

type Form = Questionnaireable & Pick<SelectorProps, 'mode'>

export const ScoreModeManager: React.FC<Props> = ({
  questionnaireableId,
  questionnaireableType,
  mode,
  disabled = false,
  hasCustomScoreConfigurationEnabled,
}) => {
  const [setMode, { loading: loadingSetMode }] = useMutation(
    PerformanceScoreModeManagerSetDocument,
    {
      extractorFn: (data) => data.performanceMutations.setModePerformanceScoreConfiguration,
      refetchQueries: [
        PerformanceProcessTemplateEditSettingsPageDocument,
        PerformanceReviewProcessReviewSettingsPageDocument,
      ],
    }
  )

  const [deleteConfig, { loading: loadingDelete }] = useMutation(
    PerformanceScoreModeManagerDeleteDocument,
    {
      extractorFn: (data) => data.performanceMutations.deletePerformanceScoreConfiguration,
      refetchQueries: [
        PerformanceProcessTemplateEditSettingsPageDocument,
        PerformanceReviewProcessReviewSettingsPageDocument,
      ],
    }
  )

  const form = useForm<Form>({
    defaultValues: {
      questionnaireableId: questionnaireableId,
      questionnaireableType: questionnaireableType,
      mode: mode,
    },
    onSubmit: async (data) => {
      if (data.mode) {
        const response = await setMode({
          variables: {
            questionnaireableId: data.questionnaireableId,
            questionnaireableType: data.questionnaireableType,
            mode: data.mode,
          },
        })
        const handled = handleResult(response, () => {})
        return handled
      }

      const response = await deleteConfig({
        variables: {
          questionnaireableId: data.questionnaireableId,
          questionnaireableType: data.questionnaireableType,
        },
      })
      const handled = handleResult(response, () => {})
      return handled
    },
  })

  const handleOnChange = (newMode: SelectorProps['mode']) => {
    form.setValues({ ...form.values, mode: newMode })
    form.handleSubmit()
  }

  return (
    <Box gap="s16">
      <Box gap="s16">
        <Text size="400" color="grey1000" weight="semibold">
          {i18n.t('performance_process.reviews.scoring_system.title')}
        </Text>
      </Box>

      <ScoreModeSelector
        mode={form.values.mode}
        disabled={disabled || loadingSetMode || loadingDelete}
        onChange={handleOnChange}
        hasCustomScoreConfigurationEnabled={hasCustomScoreConfigurationEnabled}
      />
    </Box>
  )
}
